import React, { useState, useRef } from "react";
import { LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { Box, Typography, Button, TextField, IconButton, Paper, Checkbox, FormControlLabel } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import DownloadIcon from "@mui/icons-material/Download";
import html2canvas from "html2canvas";

const initialData = [
  { x: "Feb", y: 2093 },
  { x: "Mar", y: 2100 },
  { x: "April", y: 2110 },
  { x: "May", y: 1910 }
];

const PRINT_WIDTH = 750;
const PRINT_HEIGHT = 325;

const ChartBuilder = () => {
  const [data, setData] = useState(initialData);
  const [xLabel, setXLabel] = useState("X Axis Label");
  const [yLabel, setYLabel] = useState("Y Axis Label");
  const [yStartsAtZero, setYStartsAtZero] = useState(false);

  // For display chart
  const chartRef = useRef();
  // For download-only chart
  const downloadChartRef = useRef();

  // Utility: Ensure all Y values are numbers
  const parsedData = data.map(d => ({
    x: d.x,
    y: Number(d.y) || 0
  }));

  // Calculate min/max for the y-axis domain, with some padding
  function roundToNiceNumber(value) {
    if (value > 500) {
      return Math.round(value / 50) * 50;
    } else {
      return Math.round(value / 10) * 10;
    }
  }
  
  const yValues = parsedData.map(d => d.y);
  const minY = Math.min(...yValues);
  const maxY = Math.max(...yValues);
  const PADDING_RATIO = 0.1;
  const range = maxY - minY || 1;
  const padding = Math.ceil(range * PADDING_RATIO);
  
  let domainMin = yStartsAtZero ? 0 : roundToNiceNumber(minY - padding);
  if (domainMin < 0) domainMin = 0;
  const domainMax = roundToNiceNumber(maxY + padding);
  
  const yDomain = [domainMin, domainMax];

  // Handle changes, always store numbers for 'y'
  const handleChange = (index, key, value) => {
    const newData = [...data];
    if (key === "y") {
      newData[index][key] = value === "" ? "" : Number(value);
    } else {
      newData[index][key] = value;
    }
    setData(newData);
  };

  const handleAddRow = () => {
    setData([...data, { x: "", y: "" }]);
  };

  const handleDeleteRow = (index) => {
    setData(data.filter((_, i) => i !== index));
  };

  // Download tightly cropped chart
  const handleDownload = async () => {
    if (downloadChartRef.current) {
      // html2canvas grabs ONLY the hidden chart, which has no padding
      const canvas = await html2canvas(downloadChartRef.current, {
        backgroundColor: "#fff",
        scale: 2 // for a crisp image (optional, can be 1)
      });
      const link = document.createElement("a");
      link.download = "line-chart.jpg";
      link.href = canvas.toDataURL("image/jpeg", 1.0);
      link.click();
    }
  };

  return (
    <Box sx={{
      height: "80vh",
      width: "93vw",
      display: "flex",
      flexDirection: "column",
      overflow: "hidden",
      backgroundColor: "#ffffff"
    }}>
      {/* Toolbar */}
      <Box sx={{
        width: "100%",
        py: 2,
        px: { xs: 1, sm: 3 },
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        borderBottom: "1px solid #ececec",
        flexShrink: 0,
      }}>
        <Typography variant="h5" fontWeight="bold">Custom Line Chart</Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={handleDownload}
          startIcon={<DownloadIcon />}
          sx={{ borderRadius: 2, fontWeight: 500 }}
        >
          Download Chart
        </Button>
      </Box>

      {/* Axis Labels Row & Checkbox */}
      <Box
        display="flex"
        gap={2}
        px={{ xs: 1, sm: 3 }}
        py={2}
        alignItems="center"
        borderBottom="1px solid #eee"
        flexShrink={0}
      >
        <TextField
          label="X Axis Label"
          size="small"
          value={xLabel}
          onChange={(e) => setXLabel(e.target.value)}
          sx={{ width: 180, bgcolor: "#fff" }}
        />
        <TextField
          label="Y Axis Label"
          size="small"
          value={yLabel}
          onChange={(e) => setYLabel(e.target.value)}
          sx={{ width: 180, bgcolor: "#fff" }}
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={yStartsAtZero}
              onChange={(e) => setYStartsAtZero(e.target.checked)}
              color="primary"
            />
          }
          label="Start Y Axis at 0"
          sx={{ ml: 2 }}
        />
      </Box>

      {/* Main Content: Chart | Table */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          alignItems: "stretch",
          width: "100%",
          minHeight: 0,
        }}
      >
        {/* Chart Panel */}
        <Box
          ref={chartRef}
          sx={{
            flex: 2,
            minWidth: 320,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRight: { md: "1px solid #ececec" },
            minHeight: 0,
            height: "100%",
            overflow: "hidden",
            p: 0, // no padding
            m: 0  // no margin
          }}
        >
          <LineChart
            width={PRINT_WIDTH}
            height={PRINT_HEIGHT}
            data={parsedData}
            margin={{ top: 8, right: 8, left: 8, bottom: 8 }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="x" label={{ value: xLabel, position: "insideBottom", offset: -8, fontWeight: "bold" }} />
            <YAxis
  label={{ value: yLabel, angle: -90, position: "insideLeft", fontWeight: "bold" }}
  domain={yDomain}
  allowDataOverflow={false}
  allowDecimals={true}
  tickFormatter={roundToNiceNumber}
/>
            <Tooltip />
            <Line type="monotone" dataKey="y" stroke="#1491CB" strokeWidth={3} dot />
          </LineChart>
        </Box>

        {/* Table Panel */}
        <Box
          sx={{
            flex: 1.2,
            minWidth: 260,
            display: "flex",
            flexDirection: "column",
            p: { xs: 2, md: 3 },
            minHeight: 0,
            height: "100%",
          }}
        >
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 500, flexShrink: 0 }}>
            Data Points
          </Typography>
          <Paper
            elevation={0}
            sx={{
              bgcolor: "#fff",
              border: "1px solid #ececec",
              borderRadius: 2,
              minHeight: 180,
              flex: 1,
              display: "flex",
              flexDirection: "column",
              height: "100%",
              overflow: "hidden",
              minHeight: 0,
            }}
          >
            {/* Table Header */}
            <Box display="flex" sx={{ borderBottom: "1px solid #ececec", bgcolor: "#fafbfc", flexShrink: 0 }}>
              <Box sx={{ flex: 1, px: 2, py: 1, fontWeight: 600, fontSize: 15 }}>{xLabel}</Box>
              <Box sx={{ flex: 1, px: 2, py: 1, fontWeight: 600, fontSize: 15 }}>{yLabel}</Box>
              <Box sx={{ width: 44 }} />
            </Box>
            {/* Table Body */}
            <Box
              sx={{
                flex: 1,
                overflowY: "auto",
                px: 0.5,
                py: 1,
                minHeight: 0,
              }}
            >
              {data.map((row, idx) => (
                <Box key={idx} display="flex" alignItems="center" mb={0.5}>
                  <TextField
                    value={row.x}
                    size="small"
                    variant="outlined"
                    onChange={(e) => handleChange(idx, "x", e.target.value)}
                    sx={{ flex: 1, mx: 1, bgcolor: "#fafbfc" }}
                  />
                  <TextField
                    type="number"
                    value={row.y}
                    size="small"
                    variant="outlined"
                    onChange={(e) => handleChange(idx, "y", e.target.value)}
                    sx={{ flex: 1, mx: 1, bgcolor: "#fafbfc" }}
                  />
                  <IconButton
                    color="error"
                    size="small"
                    onClick={() => handleDeleteRow(idx)}
                    disabled={data.length <= 1}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))}
            </Box>
            {/* Add Row Button */}
            <Box
              sx={{
                borderTop: "1px solid #ececec",
                px: 2,
                py: 1,
                textAlign: "center",
                bgcolor: "#fafbfc",
                flexShrink: 0,
              }}
            >
              <Button
                startIcon={<AddIcon />}
                onClick={handleAddRow}
                size="small"
                sx={{
                  borderRadius: 2,
                  fontWeight: 500,
                  px: 2
                }}
              >
                Add Row
              </Button>
            </Box>
          </Paper>
        </Box>
      </Box>

      {/* --- HIDDEN CHART FOR DOWNLOAD ONLY --- */}
      <Box
        ref={downloadChartRef}
        sx={{
          position: "absolute",
          left: -9999,
          top: 0,
          width: PRINT_WIDTH,
          height: PRINT_HEIGHT,
          background: "#fff",
          m: 0,
          p: 0,
          boxSizing: "border-box"
        }}
      >
        <LineChart
          width={PRINT_WIDTH}
          height={PRINT_HEIGHT}
          data={parsedData}
          margin={{ top: 8, right: 40, left: 8, bottom: 15 }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="x" label={{ value: xLabel, position: "insideBottom", offset: -8, fontWeight: "bold" }} />
         <YAxis
  label={{ value: yLabel, angle: -90, position: "insideLeft", fontWeight: "bold" }}
  domain={yDomain}
  allowDataOverflow={false}
  allowDecimals={true}
  tickFormatter={roundToNiceNumber}
/>

          <Line type="monotone" dataKey="y" stroke="#1491CB" strokeWidth={3} dot />
        </LineChart>
      </Box>
    </Box>
  );
};

export default ChartBuilder;
